import React from 'react';
import { Gauge, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IceScoreBadge,
} from '@ideoz/ui';
import type { ScopeStatus } from '@ideoz/core';
import { formatRelativeDate } from '@ideoz/ui/utils/dateFormatting';

export interface ScopeCardScope {
  id: string;
  title: string;
  description?: string | null;
  status: ScopeStatus;
  /** Composite ICE, or null when the scope has never been evaluated. */
  iceScore?: number | null;
  // Optional to match the facade's scope shape; a freshly-created (optimistic)
  // row may not carry timestamps until the refetch reconciles. The card guards
  // for their absence.
  updatedAt?: string;
  createdAt?: string;
}

export interface ScopeCardProps {
  scope: ScopeCardScope;
  onClick: (id: string) => void;
  onEdit: (scope: ScopeCardScope) => void;
  onDelete: (id: string) => void;
  /** Queues an evaluation. The ⋯ menu is the only place this can be started. */
  onEvaluate: (id: string) => void;
  /** Opens the ICE details popup. */
  onOpenIce: (id: string) => void;
  /** An evaluation is queued or running for this scope. */
  isScoring?: boolean;
}

export const ScopeCard: React.FC<ScopeCardProps> = ({
  scope,
  onClick,
  onEdit,
  onDelete,
  onEvaluate,
  onOpenIce,
  isScoring = false,
}) => {
  // The status still drives the left edge; the flag on the right is now ICE.

  return (
    <div
      className={`relative flex items-center border border-border rounded-xl bg-surface cursor-pointer hover:shadow-sm p-4 hover:bg-blue-50 hover:border-blue-200 dark:hover:bg-secondary dark:hover:border-secondary transition-all`}
      onClick={() => onClick(scope.id)}
      data-testid="scope-card"
    >
      {/* Main content */}
      <div className="flex-1">
        <p className="text-sm text-gray-900 dark:text-gray-100 leading-snug">{scope.title}</p>
        {scope.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
            {scope.description}
          </p>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {scope?.createdAt ? `Created ${formatRelativeDate(scope.createdAt)}` : ''}
        </div>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <IceScoreBadge
          ice={scope.iceScore ?? null}
          isScoring={isScoring}
          // Only a scored badge opens the popup — there is nothing to explain
          // about a scope that has never been evaluated.
          onClick={scope.iceScore != null && !isScoring ? () => onOpenIce(scope.id) : undefined}
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <MoreHorizontal className="h-4 w-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44 p-2 rounded-xl">
            <DropdownMenuItem
              onClick={() => onEdit(scope)}
              className="gap-2 cursor-pointer rounded-lg"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onEvaluate(scope.id)}
              disabled={isScoring}
              className="gap-2 cursor-pointer rounded-lg"
            >
              <Gauge className="h-3.5 w-3.5" />{' '}
              {scope.iceScore != null ? 'Re-evaluate' : 'Evaluate'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(scope.id)}
              className="gap-2 cursor-pointer rounded-lg text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ScopeCard;
